import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './GlimpseSection.css';

const images = Array.from({ length: 9 }, (_, i) => ({
  src: `/images/gallery/${i + 1}.jpeg`,
  alt: `Gallery image ${i + 1}`
}));

// Double the images array for continuous loop
const allImages = [...images, ...images];

// Dynamic item width based on screen size
const getItemWidth = () => {
  if (typeof window === 'undefined') return 180;
  const width = window.innerWidth;
  if (width <= 600) return window.innerWidth; // Full width on very small screens
  if (width <= 900) return width / 2; // Half width on small screens
  if (width <= 1200) return width / 3; // Third width on medium screens
  return width / 4; // Quarter width on large screens
};

const GlimpseSection: React.FC = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const trackRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState(0);
  const [frameIndex, setFrameIndex] = useState(0);
  const [itemWidth, setItemWidth] = useState(180);

  // Update item width on resize
  useEffect(() => {
    const updateWidth = () => {
      setItemWidth(getItemWidth());
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Track animation
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let animationFrame: number;
    let scrollAmount = 0;
    const speed = Math.max(0.8, itemWidth * 0.004); // Adjust speed based on item width

    function animate() {
      if (!track) return;
      scrollAmount += speed;
      if (scrollAmount >= track.scrollWidth / 2) {
        scrollAmount = 0;
      }
      track.scrollLeft = scrollAmount;
      setScroll(scrollAmount);
      animationFrame = requestAnimationFrame(animate);
    }
    animate();
    return () => cancelAnimationFrame(animationFrame);
  }, [itemWidth]); // Re-run when itemWidth changes

  // Calculate which image is at the left edge (virtual frame)
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    // The left frame is at the start (left: 0)
    // Find the image whose left edge is closest to the frame (scroll position)
    const index = Math.round(scroll / itemWidth) % images.length;
    setFrameIndex((index + images.length) % images.length);
  }, [scroll, itemWidth]);

  return (
    <section 
      className="glimpse-section" 
      id="glimpse" 
      ref={ref}
      aria-labelledby="glimpse-title"
    >
      <div className="glimpse-container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 id="glimpse-title" className="section-title">Experience Innovation</h2>
          <p className="section-description">
            Where future tech leaders are born
          </p>
        </motion.div>

        <div className="glimpse-track-wrapper left-frame">
          {/* No frame element here! */}
          <div className="glimpse-track" ref={trackRef} style={{overflow: 'hidden'}}>
            <div className="glimpse-track-flex">
              {allImages.map((img, idx) => {
                // Zoom the image that is at the left edge (virtual frame)
                const isInFrame = idx % images.length === frameIndex;
                return (
                  <div 
                    key={idx}
                    className={`carousel-item${isInFrame ? ' zoomed' : ''}`}
                    style={{flex: `0 0 ${itemWidth}px`, margin: '0 8px'}}
                  >
                    <img 
                      src={img.src} 
                      alt={img.alt}
                      className="carousel-img"
                      loading="lazy"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlimpseSection;
